import React, { useEffect, useMemo, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import InputAdornment from "@material-ui/core/InputAdornment";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PictureAsPdfIcon,
  AttachMoney as AttachMoneyIcon,
  MoreVert as MoreVertIcon,
  ReceiptOutlined as ReceiptOutlinedIcon,
} from "@material-ui/icons";
import Tooltip from "@material-ui/core/Tooltip";
import axios from "../../services";
import toaster from "../../utils/toaster";
import QuotationPDF from "../QuotationPDF/QuotationPDF";
import { hasQuotationPricingDetails } from "../../utils/quotationPricingStatus";
import PageHeader from "../ui/PageHeader";
import StatusBadge from "../ui/StatusBadge";
import Card from "../ui/Card";
import EmptyState from "../ui/EmptyState";

const useStyles = makeStyles((theme) => ({
  introBox: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "130px",
    marginTop: "60px",
  },
  primaryText: {
    fontWeight: 600,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  secondaryText: {
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  /** App shell ~100px sidebar + this page’s 130px offset; avoid maxWidth cap clipping wide tables */
  tableWrap: {
    marginLeft: "130px",
    marginTop: theme.spacing(2),
    width: "calc(100vw - 100px - 130px - 16px)",
    maxWidth: "none",
    boxSizing: "border-box",
    overflowX: "auto",
    paddingRight: theme.spacing(1),
  },
  tableScroller: {
    overflowX: "auto",
    width: "100%",
  },
  tableMinWidth: {
    minWidth: 1040,
  },
  stickyActionHeader: {
    position: "sticky",
    right: 0,
    zIndex: 4,
    background: "var(--gray-50)",
    boxShadow: "-6px 0 12px rgba(15, 23, 42, 0.04)",
    minWidth: 120,
    whiteSpace: "nowrap",
  },
  stickyActionCell: {
    position: "sticky",
    right: 0,
    zIndex: 2,
    background: "var(--surface)",
    boxShadow: "-6px 0 12px rgba(15, 23, 42, 0.04)",
    minWidth: 120,
    whiteSpace: "nowrap",
    verticalAlign: "top",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 15,
    fontWeight: 600,
    color: "var(--text)",
  },
  countBadge: {
    display: "inline-flex",
    alignItems: "center",
    height: 20,
    padding: "0 8px",
    borderRadius: 999,
    background: "var(--gray-100)",
    color: "var(--text-secondary)",
    fontSize: 12,
    fontWeight: 600,
  },
  actionBar: {
    marginLeft: "130px",
    marginTop: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  searchField: {
    minWidth: 280,
    maxWidth: 420,
  },
}));

const getPricingIconColor = (added) => (added ? "green" : "red");

const publicIntakeChip = (row) => {
  if (row.intakeChannel === "website_quote_form") {
    return <StatusBadge tone="info">Filled from website</StatusBadge>;
  }
  if (row.intakeChannel !== "public_quote_form") return null;
  if (row.customerSubmitted) {
    return <StatusBadge tone="danger">Filled by customer</StatusBadge>;
  }
  if (row.status === "draft_customer") {
    return <StatusBadge tone="neutral">Public draft</StatusBadge>;
  }
  return <StatusBadge tone="neutral">Public intake</StatusBadge>;
};

const QuotationsPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [rowMenu, setRowMenu] = useState({ anchor: null, row: null });
  const closeRowMenu = () => setRowMenu({ anchor: null, row: null });

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const name = (row.customerName || "").toLowerCase();
      const email = (row.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [rows, search]);

  const loadData = async () => {
    try {
      const response = await axios.get("quotations");
      setRows(response.data || []);
    } catch (err) {
      toaster(err?.response?.data?.message || "Failed to load quotations", { type: "error" });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(location.search).get("q");
    setSearch(q != null ? q : "");
  }, [location.search]);

  const downloadQuotationPdf = async (row) => {
    setPdfLoadingId(row._id);
    try {
      const response = await axios.get(`quotations/${row._id}`);
      const blob = await pdf(<QuotationPDF data={response.data} />).toBlob();
      const safeName = (row.customerName || "quotation").replace(/[^\w-]+/g, "_");
      saveAs(blob, `Quotation-${safeName}-${String(row._id).slice(-6)}.pdf`);
      toaster("Quotation PDF downloaded");
    } catch (err) {
      console.error("Quotation PDF download:", err);
      const msg =
        err?.response?.data?.message || err?.message || "Failed to generate quotation PDF";
      toaster(msg, { type: "error" });
    } finally {
      setPdfLoadingId(null);
    }
  };

  const deleteQuotation = async (id) => {
    try {
      await axios.delete(`quotations/${id}`);
      toaster("Quotation deleted successfully");
      loadData();
    } catch (err) {
      toaster(err?.response?.data?.message || "Failed to delete quotation", { type: "error" });
    }
  };

  return (
    <>
      <Box className={classes.introBox}>
        <PageHeader
          title="Quotations"
          description="Manage saved quotations"
          actions={
            <Button variant="contained" color="primary" onClick={() => history.push("/quotations/new")}>
              New Quotation
            </Button>
          }
        />
      </Box>
      <Box className={classes.tableWrap}>
        <Card>
          <div className={classes.cardHeader}>
            <span className={classes.cardTitle}>
              Quotations
              <span className={classes.countBadge}>{filteredRows.length}</span>
            </span>
            <TextField
              className={classes.searchField}
              variant="outlined"
              size="small"
              placeholder="Search by customer name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ "aria-label": "Search quotations by name or email" }}
            />
          </div>
          <TableContainer className={classes.tableScroller}>
            <Table size="small" className={classes.tableMinWidth}>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Origin</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Total Items</TableCell>
                  <TableCell>Total Quantity</TableCell>
                  <TableCell>Total CFT</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell className={classes.stickyActionHeader} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell>{row.customerName}</TableCell>
                    <TableCell>{row.email || "—"}</TableCell>
                    <TableCell>{row.originCity || row.originAddress1}</TableCell>
                    <TableCell>{row.destinationCity || row.destinationAddress1}</TableCell>
                    <TableCell>{row.totalItemsCount}</TableCell>
                    <TableCell>{row.totalQuantity}</TableCell>
                    <TableCell>{row.totalCft}</TableCell>
                    <TableCell>{publicIntakeChip(row) || "—"}</TableCell>
                    <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell className={classes.stickyActionCell} align="right">
                      <Tooltip title="View">
                        <IconButton onClick={() => history.push(`/quotations/${row._id}`)} aria-label="View quotation">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => history.push(`/quotations/${row._id}/edit`)} aria-label="Edit quotation">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        aria-label="More actions"
                        onClick={(e) => setRowMenu({ anchor: e.currentTarget, row })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredRows.length === 0 && (
            <EmptyState
              icon={<ReceiptOutlinedIcon fontSize="inherit" />}
              title={rows.length === 0 ? "No quotations yet" : "No quotations match your search"}
              hint={
                rows.length === 0
                  ? "Create your first quotation to see it here."
                  : "Try a different name or email."
              }
            />
          )}
        </Card>
      </Box>

      <Menu
        anchorEl={rowMenu.anchor}
        open={Boolean(rowMenu.anchor)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            history.push(`/create-job?quotationId=${rowMenu.row?._id}`);
            closeRowMenu();
          }}
        >
          <ListItemIcon style={{ minWidth: 36 }}>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create job from quotation</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            history.push(`/quotations/${rowMenu.row?._id}/pricing`);
            closeRowMenu();
          }}
        >
          <ListItemIcon style={{ minWidth: 36 }}>
            <AttachMoneyIcon
              fontSize="small"
              style={{ color: getPricingIconColor(rowMenu.row ? hasQuotationPricingDetails(rowMenu.row) : false) }}
            />
          </ListItemIcon>
          <ListItemText>Pricing & PDF</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={pdfLoadingId === rowMenu.row?._id}
          onClick={() => {
            downloadQuotationPdf(rowMenu.row);
            closeRowMenu();
          }}
        >
          <ListItemIcon style={{ minWidth: 36 }}>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Generate quotation PDF</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          style={{ color: "var(--danger-strong)" }}
          onClick={() => {
            deleteQuotation(rowMenu.row?._id);
            closeRowMenu();
          }}
        >
          <ListItemIcon style={{ minWidth: 36 }}>
            <DeleteIcon fontSize="small" style={{ color: "var(--danger-strong)" }} />
          </ListItemIcon>
          <ListItemText>Delete quotation</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default QuotationsPage;
